import { useState } from 'react'
import { formatNumber, niceMax, shortDay, ticks } from './scale'
import { useWidth } from './use-width'
import styles from './charts.module.css'

type Props = {
  readonly label: string
  readonly days: readonly string[]
  readonly value: (day: string) => number | null
  readonly color: string
  readonly unit: string
  readonly height?: number
}

const PAD = { top: 12, right: 12, bottom: 24, left: 36 }

export function LineChart({ label, days, value, color, unit, height = 200 }: Props) {
  const { ref, width } = useWidth<HTMLDivElement>()
  const [hover, setHover] = useState<number | null>(null)
  const values = days.map(value)
  const max = niceMax(Math.max(0, ...values.map((v) => v ?? 0)))
  const plotW = width - PAD.left - PAD.right
  const plotH = height - PAD.top - PAD.bottom
  const step = plotW / Math.max(1, days.length - 1)
  const x = (i: number) => PAD.left + i * step
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH
  const path = values
    .map((v, i) => (v === null ? null : `${x(i)},${y(v)}`))
    .reduce<string[]>((acc, p, i) => {
      if (p === null) return acc
      acc.push(`${values[i - 1] === null || i === 0 ? 'M' : 'L'}${p}`)
      return acc
    }, [])
    .join(' ')
  const labelEvery = Math.ceil(days.length / Math.max(1, Math.floor(plotW / 48)))
  const hv = hover === null ? null : values[hover]

  return (
    <div ref={ref} className={styles.chart}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={label}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const box = e.currentTarget.getBoundingClientRect()
          const i = Math.round((e.clientX - box.left - PAD.left) / step)
          setHover(i >= 0 && i < days.length ? i : null)
        }}
      >
        {ticks(max).map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} className={styles.grid} />
            <text x={PAD.left - 6} y={y(t)} className={styles.axis} textAnchor="end" dominantBaseline="middle">
              {formatNumber(t)}
            </text>
          </g>
        ))}
        {days.map((d, i) =>
          i % labelEvery === 0 ? (
            <text key={d} x={x(i)} y={height - 6} className={styles.axis} textAnchor="middle">
              {shortDay(d)}
            </text>
          ) : null,
        )}
        <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {hover !== null && (
          <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotH} className={styles.crosshair} />
        )}
        {hover !== null && hv !== null && hv !== undefined && (
          <circle cx={x(hover)} cy={y(hv)} r={4} fill={color} className={styles.dot} />
        )}
      </svg>
      {hover !== null && (
        <div className={styles.tooltip} style={{ left: Math.min(width - 160, Math.max(0, x(hover) - 80)) }} role="status">
          <div className={styles.tooltipTitle}>{shortDay(days[hover] ?? '')}</div>
          <div className={styles.tooltipRow}>
            <span>{unit}</span>
            <span className={styles.tooltipValue}>{hv === null || hv === undefined ? 'нет данных' : formatNumber(hv)}</span>
          </div>
        </div>
      )}
    </div>
  )
}
